import { 
  X, 
  Layout, 
  Plus, 
  Trash2, 
  GripVertical,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  Settings2,
  Table,
  Type,
  Badge,
  List,
  LayoutGrid
} from "lucide-react"
import type { 
  LayerExperienceSurfaceDefinition, 
  LayerExperienceSurfaceBlockDefinition 
} from "../../types/layer-experience"
import type { MapSurfaceDefinitionEditorProps } from "../../contracts/map-interactivity"
import { Label } from "../../internal/primitives/label"
import { Input } from "../../internal/primitives/input"
import { Separator } from "../../internal/primitives/separator"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../internal/primitives/select"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../internal/primitives/accordion"
import { cn } from "../../lib/utils"

export function MapSurfaceDefinitionEditor({
  mode,
  value,
  availableFields,
  availablePipelines = [],
  onChange,
  onSave,
  onCancel,
  className
}: MapSurfaceDefinitionEditorProps) {
  const SUMMARY_ROWS_SECTION_ID = "__surface_summary_rows__"
  const SUMMARY_SUBTITLE_SECTION_ID = "__surface_summary_subtitle__"
  const SUMMARY_ROW_SECTION_KINDS = new Set(["details_grid", "stats_row", "list"])
  const isHoverPopup = value.surface_kind === "hover_popup"
  const isAmbiguousSelectionSummary = value.surface_kind === "ambiguous_selection_summary"
  const showStructuredIdentityEditor = isHoverPopup || isAmbiguousSelectionSummary

  const handleUpdate = (patch: Partial<LayerExperienceSurfaceDefinition>) => {
    onChange({ ...value, ...patch })
  }

  const handleAddSection = () => {
    const nextSection: LayerExperienceSurfaceBlockDefinition = {
      id: `section-${Date.now()}`,
      kind: "details_grid",
      label: "New Section",
      items: []
    }
    handleUpdate({ sections: [...value.sections, nextSection] })
  }

  const handleRemoveSection = (id: string) => {
    handleUpdate({ sections: value.sections.filter(s => s.id !== id) })
  }

  const handleUpdateSection = (id: string, patch: Partial<LayerExperienceSurfaceBlockDefinition>) => {
    handleUpdate({
      sections: value.sections.map(s => s.id === id ? { ...s, ...patch } : s)
    })
  }

  const handleRemoveSectionItem = (section: LayerExperienceSurfaceBlockDefinition, index: number) => {
    const nextItems = (section.items as any[] | undefined)?.filter((_, itemIndex) => itemIndex !== index) ?? []
    handleUpdateSection(section.id!, { items: nextItems })
  }

  const handleAddSectionItem = (section: LayerExperienceSurfaceBlockDefinition) => {
    const defaultField = availableFields[0]?.id ?? ""
    const defaultPipelineId = availablePipelines[0]?.pipeline_id ?? ""
    const nextItem =
      section.kind === "action_row"
        ? { label: "Run action", pipeline_id: defaultPipelineId }
        : { label: "", field: defaultField }
    handleUpdateSection(section.id!, { items: [...((section.items as any[]) ?? []), nextItem] })
  }

  const summaryRowsSection =
    value.sections.find(section => section.id === SUMMARY_ROWS_SECTION_ID) ??
    value.sections.find(section => SUMMARY_ROW_SECTION_KINDS.has(section.kind))
  const summarySubtitleSection =
    value.sections.find(section => section.id === SUMMARY_SUBTITLE_SECTION_ID) ??
    value.sections.find(section => section.kind === "text")
  const summaryRows = ((summaryRowsSection?.items as Array<{ label?: string; field?: string }> | undefined) ?? [])
    .filter(item => typeof item?.field === "string")
  const summarySubtitleField =
    typeof summarySubtitleSection?.binding?.field === "string" ? String(summarySubtitleSection.binding.field) : "__none__"
  const titleField =
    typeof value.title_binding?.field === "string" ? String(value.title_binding.field) : "__none__"
  const visibleSections = value.sections.filter(section => {
    if (!isHoverPopup) {
      return true
    }
    return section !== summaryRowsSection && section !== summarySubtitleSection
  })

  const updateSummaryRowsSection = (nextItems: Array<{ label?: string; field?: string }>) => {
    if (summaryRowsSection) {
      handleUpdate({
        sections: value.sections.map(section =>
          section === summaryRowsSection
            ? {
                ...section,
                kind: SUMMARY_ROW_SECTION_KINDS.has(section.kind) ? section.kind : "details_grid",
                label: section.label || "Summary",
                items: nextItems,
              }
            : section,
        ),
      })
      return
    }
    handleUpdate({
      sections: [
        ...value.sections,
        {
          id: SUMMARY_ROWS_SECTION_ID,
          kind: "details_grid",
          label: "Summary",
          items: nextItems,
        },
      ],
    })
  }

  const updateSummaryRowAt = (index: number, patch: Partial<{ label?: string; field?: string }>) => {
    const nextItems = summaryRows.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item))
    updateSummaryRowsSection(nextItems)
  }

  const moveSummaryRow = (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction
    if (nextIndex < 0 || nextIndex >= summaryRows.length) {
      return
    }
    const nextItems = [...summaryRows]
    const [moved] = nextItems.splice(index, 1)
    nextItems.splice(nextIndex, 0, moved)
    updateSummaryRowsSection(nextItems)
  }

  const addSummaryRow = () => {
    const defaultField = availableFields[0]?.id ?? ""
    if (!defaultField) {
      return
    }
    updateSummaryRowsSection([...summaryRows, { label: "", field: defaultField }])
  }

  const removeSummaryRow = (index: number) => {
    updateSummaryRowsSection(summaryRows.filter((_, itemIndex) => itemIndex !== index))
  }

  const updateSubtitleField = (field: string) => {
    if (summarySubtitleSection) {
      handleUpdate({
        sections: value.sections.map(section =>
          section === summarySubtitleSection
            ? {
                ...section,
                id: section.id || SUMMARY_SUBTITLE_SECTION_ID,
                kind: "text",
                label: section.label || "Subtitle",
                binding: field === "__none__" ? null : { field },
                empty_state: null,
              }
            : section,
        ),
      })
      return
    }
    if (field === "__none__") {
      return
    }
    handleUpdate({
      sections: [
        ...value.sections,
        {
          id: SUMMARY_SUBTITLE_SECTION_ID,
          kind: "text",
          label: "Subtitle",
          binding: { field },
          items: [],
        },
      ],
    })
  }

  return (
    <div className={cn("flex flex-col h-full bg-background border rounded-lg overflow-hidden shadow-xl", className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded bg-primary/10 text-primary">
            <Layout className="w-4 h-4" />
          </div>
          <div className="flex flex-col">
            <h3 className="text-sm font-bold uppercase tracking-tight">
              {mode === "create" ? "New Surface Definition" : "Edit Surface"}
            </h3>
            <p className="text-[10px] text-muted-foreground font-medium">Design custom detail panels and card layouts</p>
          </div>
        </div>
        <button onClick={onCancel} className="p-1 hover:bg-muted rounded-full transition-colors text-muted-foreground">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Core Metadata */}
        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-1.5">
            <Label className="text-[10px] uppercase font-bold text-muted-foreground/70">Surface ID</Label>
            <Input 
              value={value.surface_id} 
              onChange={(e) => handleUpdate({ surface_id: e.target.value })}
              placeholder="e.g. asset_details"
              className="h-8 text-xs font-mono"
            />
          </div>
          <div className="grid gap-1.5">
            <Label className="text-[10px] uppercase font-bold text-muted-foreground/70">Kind</Label>
            <Select 
              value={value.surface_kind} 
              onValueChange={(val: any) => handleUpdate({ surface_kind: val })}
            >
              <SelectTrigger className="h-8 text-xs bg-background capitalize">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="side_panel">Side Panel</SelectItem>
                <SelectItem value="bottom_sheet">Bottom Sheet</SelectItem>
                <SelectItem value="floating_summary_card">Floating Card</SelectItem>
                <SelectItem value="hover_popup">Custom Hover</SelectItem>
                <SelectItem value="ambiguous_selection_summary">Ambiguous Selection Summary</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 grid gap-1.5">
            <Label className="text-[10px] uppercase font-bold text-muted-foreground/70">Display Label</Label>
            <Input 
              value={value.label || ""} 
              onChange={(e) => handleUpdate({ label: e.target.value })}
              placeholder="e.g. Asset Inspector"
              className="h-8 text-xs"
            />
          </div>
        </div>

        <Separator />

        {/* Data Binding */}
        <div className="grid gap-4">
          <div className="flex flex-col gap-0.5">
            <Label className="text-[11px] font-bold uppercase text-muted-foreground tracking-tight">Data Binding</Label>
            <p className="text-[10px] text-muted-foreground">Define what data context this surface consumes</p>
          </div>
          
          <div className="grid gap-3 p-3 border rounded-lg bg-muted/5">
            <div className="grid gap-1.5">
              <Label className="text-[10px] uppercase font-bold text-muted-foreground/60">Primary Data Source</Label>
              <Select 
                value={(value.data_binding as any)?.source || "selected_feature"} 
                onValueChange={(val) => handleUpdate({ data_binding: { source: val } })}
              >
                <SelectTrigger className="h-8 text-xs bg-background">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="selected_feature">Selected Feature</SelectItem>
                  <SelectItem value="hovered_feature">Hovered Feature</SelectItem>
                  <SelectItem value="active_preset">Active Preset Context</SelectItem>
                  <SelectItem value="resolver_result">Resolver Result</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {showStructuredIdentityEditor ? (
          <>
            <Separator />

            <div className="grid gap-4">
              <div className="flex flex-col gap-0.5">
                <Label className="text-[11px] font-bold uppercase text-muted-foreground tracking-tight">
                  {isAmbiguousSelectionSummary ? "Ambiguous Selection Summary" : "Hover Popup Identity"}
                </Label>
                <p className="text-[10px] text-muted-foreground">
                  {isAmbiguousSelectionSummary
                    ? "Choose the fields shown only when overlapping features need to be disambiguated"
                    : "Choose the title, subtitle, and quick facts shown at the top of the hover popup"}
                </p>
              </div>

              <div className="grid gap-3 p-3 border rounded-lg bg-muted/5">
                <div className="grid grid-cols-2 gap-3">
                  <div className="grid gap-1.5">
                    <Label className="text-[10px] uppercase font-bold text-muted-foreground/60">Title Field</Label>
                    <Select
                      value={titleField}
                      onValueChange={(field) => handleUpdate({ title_binding: field === "__none__" ? null : { field } })}
                    >
                      <SelectTrigger className="h-8 text-xs bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">No title binding</SelectItem>
                        {availableFields.map(field => (
                          <SelectItem key={field.id} value={field.id}>
                            {field.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-1.5">
                    <Label className="text-[10px] uppercase font-bold text-muted-foreground/60">Subtitle Field</Label>
                    <Select value={summarySubtitleField} onValueChange={updateSubtitleField}>
                      <SelectTrigger className="h-8 text-xs bg-background">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">No subtitle binding</SelectItem>
                        {availableFields.map(field => (
                          <SelectItem key={field.id} value={field.id}>
                            {field.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid gap-1.5">
                  <div className="flex items-center justify-between">
                    <Label className="text-[10px] uppercase font-bold text-muted-foreground/60">Summary Rows</Label>
                    <button
                      type="button"
                      onClick={addSummaryRow}
                      disabled={availableFields.length === 0}
                      className="text-[10px] font-bold text-primary flex items-center gap-1 hover:underline disabled:opacity-50 disabled:no-underline"
                    >
                      <Plus className="w-3 h-3" /> Add Row
                    </button>
                  </div>
                  <div className="p-2 border rounded-md bg-background/60 space-y-2">
                    {summaryRows.length > 0 ? (
                      summaryRows.map((item, index) => (
                        <div key={`${item.field ?? "summary"}:${index}`} className="flex items-center gap-2">
                          <Select
                            value={item.field || ""}
                            onValueChange={(field) => updateSummaryRowAt(index, { field })}
                          >
                            <SelectTrigger className="h-7 text-[10px] flex-1 bg-background">
                              <SelectValue placeholder="Field" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableFields.map(field => (
                                <SelectItem key={field.id} value={field.id}>
                                  {field.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            value={item.label || ""}
                            onChange={(e) => updateSummaryRowAt(index, { label: e.target.value })}
                            className="h-7 text-[10px] w-[140px]"
                            placeholder="Custom label"
                          />
                          <button
                            type="button"
                            onClick={() => moveSummaryRow(index, -1)}
                            disabled={index === 0}
                            className="p-1 text-muted-foreground/50 hover:text-foreground disabled:opacity-30"
                          >
                            <ChevronUp className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveSummaryRow(index, 1)}
                            disabled={index === summaryRows.length - 1}
                            className="p-1 text-muted-foreground/50 hover:text-foreground disabled:opacity-30"
                          >
                            <ChevronDown className="w-3 h-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeSummaryRow(index)}
                            className="p-1 text-muted-foreground/40 hover:text-destructive"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="px-1 py-3 text-[10px] text-muted-foreground italic">
                        No summary rows selected
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <Separator />
          </>
        ) : null}

        {!isAmbiguousSelectionSummary ? (
          <div className="grid gap-4 pb-4">
            <div className="flex items-center justify-between">
              <div className="flex flex-col gap-0.5">
                <Label className="text-[11px] font-bold uppercase text-muted-foreground tracking-tight">
                  {isHoverPopup ? "Additional Sections" : "Layout Sections"}
                </Label>
                <p className="text-[10px] text-muted-foreground">
                  {isHoverPopup
                    ? "Optional badges, actions, and supporting sections shown below the hover identity block"
                    : "Components that make up the surface body"}
                </p>
              </div>
              <button 
                onClick={handleAddSection}
                className="text-[10px] font-bold text-primary flex items-center gap-1 hover:underline"
              >
                <Plus className="w-3 h-3" /> Add Section
              </button>
            </div>

            <Accordion type="multiple" className="space-y-2">
              {visibleSections.map((section) => (
                <AccordionItem key={section.id} value={section.id!} className="border rounded-lg bg-card overflow-hidden">
                  <AccordionTrigger className="hover:no-underline py-2.5 px-3">
                    <div className="flex items-center gap-3 text-left">
                      <GripVertical className="w-3 h-3 text-muted-foreground/30" />
                      <div className="p-1 rounded bg-muted">
                         <SectionKindIcon kind={section.kind} className="w-3.5 h-3.5 text-muted-foreground" />
                      </div>
                      <div className="flex flex-col">
                         <span className="text-xs font-bold">{section.label || "Untitled Section"}</span>
                         <span className="text-[9px] font-mono text-muted-foreground uppercase">{section.kind.replace("_", " ")}</span>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-3 pb-3 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="grid gap-1.5">
                        <Label className="text-[10px] uppercase font-bold text-muted-foreground/60">Label</Label>
                        <Input 
                          value={section.label || ""} 
                          onChange={(e) => handleUpdateSection(section.id!, { label: e.target.value })}
                          className="h-7 text-xs"
                        />
                      </div>
                      <div className="grid gap-1.5">
                        <Label className="text-[10px] uppercase font-bold text-muted-foreground/60">Kind</Label>
                        <Select 
                          value={section.kind} 
                          onValueChange={(val: any) => handleUpdateSection(section.id!, { kind: val })}
                        >
                          <SelectTrigger className="h-7 text-xs bg-background">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="details_grid">Details Grid</SelectItem>
                            <SelectItem value="badge_row">Badge Row</SelectItem>
                            <SelectItem value="stats_row">Stats Row</SelectItem>
                            <SelectItem value="list">Item List</SelectItem>
                            <SelectItem value="action_row">Action Row</SelectItem>
                            <SelectItem value="text">Prose / Description</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid gap-1.5">
                      <Label className="text-[10px] uppercase font-bold text-muted-foreground/60">Data Items</Label>
                      <div className="p-2 border rounded-md bg-muted/5 space-y-2">
                         {(section.items as any[])?.map((item, i) => (
                           <div key={i} className="flex items-center gap-2">
                              <Input 
                                value={item.label || ""} 
                                onChange={(e) => {
                                  const nextItems = [...(section.items as any[])]
                                  nextItems[i] = { ...nextItems[i], label: e.target.value }
                                  handleUpdateSection(section.id!, { items: nextItems })
                                }}
                                className="h-7 text-[10px] flex-1"
                                placeholder="Label"
                              />
                              {section.kind === "action_row" ? (
                                <Select
                                  value={item.pipeline_id || ""}
                                  onValueChange={(val) => {
                                    const nextItems = [...(section.items as any[])]
                                    nextItems[i] = { ...nextItems[i], pipeline_id: val }
                                    handleUpdateSection(section.id!, { items: nextItems })
                                  }}
                                >
                                  <SelectTrigger className="h-7 text-[10px] w-[160px] font-mono">
                                    <SelectValue placeholder="Pipeline" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {availablePipelines.map(pipeline => (
                                      <SelectItem key={pipeline.pipeline_id} value={pipeline.pipeline_id}>
                                        {pipeline.label || pipeline.pipeline_id}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Select 
                                  value={item.field || ""} 
                                  onValueChange={(val) => {
                                    const nextItems = [...(section.items as any[])]
                                    nextItems[i] = { ...nextItems[i], field: val }
                                    handleUpdateSection(section.id!, { items: nextItems })
                                  }}
                                >
                                  <SelectTrigger className="h-7 text-[10px] w-[120px] font-mono">
                                    <SelectValue placeholder="Field" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {availableFields.map(f => (
                                      <SelectItem key={f.id} value={f.id}>{f.id}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                              <button
                                type="button"
                                onClick={() => handleRemoveSectionItem(section, i)}
                                className="p-1 text-muted-foreground/40 hover:text-destructive"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                           </div>
                         ))}
                         <button 
                           onClick={() => handleAddSectionItem(section)}
                           className="text-[10px] font-bold text-primary flex items-center gap-1 hover:underline px-1"
                         >
                           <Plus className="w-3 h-3" /> Add {section.kind === "action_row" ? "Launcher" : "Item"}
                         </button>
                      </div>
                    </div>

                    <div className="flex justify-end pt-2">
                      <button 
                        onClick={() => handleRemoveSection(section.id!)}
                        className="text-[10px] font-bold text-destructive flex items-center gap-1 hover:underline"
                      >
                        <Trash2 className="w-3 h-3" /> Remove Section
                      </button>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            
            {visibleSections.length === 0 && (
              <div className="p-8 border border-dashed rounded-lg bg-muted/5 flex flex-col items-center justify-center text-center">
                <LayoutGrid className="w-8 h-8 text-muted-foreground/20 mb-2" />
                <p className="text-[11px] font-medium text-muted-foreground italic">
                  {isHoverPopup ? "No additional sections defined" : "No layout sections defined"}
                </p>
              </div>
            )}
          </div>
        ) : null}
      </div>

      {/* Footer */}
      <div className="px-4 py-3 border-t bg-muted/10 flex items-center justify-end gap-3">
        <button 
          onClick={onCancel}
          className="px-3 py-1.5 text-xs font-bold text-muted-foreground hover:text-foreground transition-colors"
        >
          Cancel
        </button>
        <button 
          onClick={onSave}
          className="px-4 py-1.5 bg-primary text-primary-foreground rounded text-xs font-bold shadow-sm hover:bg-primary/90 transition-colors"
        >
          Save Surface
        </button>
      </div>
    </div>
  )
}

function SectionKindIcon({ kind, className }: { kind: string; className?: string }) {
  switch (kind) {
    case "details_grid": return <LayoutGrid className={className} />
    case "badge_row": return <Badge className={className} />
    case "stats_row": return <Settings2 className={className} />
    case "list": return <List className={className} />
    case "action_row": return <ChevronRight className={className} />
    case "text": return <Type className={className} />
    default: return <Table className={className} />
  }
}
